/* eslint-disable react/prop-types */
import { useState } from 'react';
import { Alert, Box, Button, IconButton, MenuItem, Slider, Stack, TextField, Typography } from '@mui/material';
import { Iconify } from 'src/components';
import useCallAudioPlayer from './useCallAudioPlayer';
import { recordingStateMessage } from './recordingState';

const clock = (seconds) => `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;
// `unavailableText` (why there is no recording) wins over the provider state's text.
// `compact` (call review window): no volume slider and no browser-controls switch.
export default function CallAudioPlayer({ src, recordingState, unavailableText, compact }) {
  const { audioRef, media, update, fail, toggle, seek, setRate, setVolume, retry } = useCallAudioPlayer({ src });
  const [native, setNative] = useState(false);
  if (!src)
    return <Alert severity="info">{unavailableText || recordingStateMessage(recordingState)?.detail || 'Audio unavailable. Saved transcript and AI review remain available below.'}</Alert>;
  return (
    <Stack spacing={1}>
      <Box
        component="audio"
        ref={audioRef}
        preload="metadata"
        controls={native}
        aria-label="Call recording"
        onLoadedMetadata={update}
        onDurationChange={update}
        onTimeUpdate={update}
        onPlay={update}
        onPause={update}
        onEnded={update}
        onRateChange={update}
        onVolumeChange={update}
        onError={fail}
        sx={{ display: native ? 'block' : 'none', width: '100%' }}
      />
      {!native && (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <IconButton
            aria-label={media.playing ? 'Pause recording' : 'Play recording'}
            onClick={toggle}
            sx={{
              width: 44,
              height: 44,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': { bgcolor: 'primary.dark' },
            }}
          >
            <Iconify icon={media.playing ? 'eva:pause-circle-fill' : 'eva:play-circle-fill'} />
          </IconButton>
          <IconButton
            aria-label="Back 10 seconds"
            disabled={!media.ready || !media.duration}
            onClick={() => seek(media.time - 10)}
            sx={{ width: 44, height: 44 }}
          >
            <Iconify icon="material-symbols:replay-10-rounded" />
          </IconButton>
          <Box sx={{ flex: 1, minWidth: 120, px: 1 }}>
            <Slider
              aria-label="Recording position"
              min={0}
              max={media.duration || 1}
              value={Math.min(media.time, media.duration || 1)}
              step={0.1}
              disabled={!media.ready || !media.duration}
              onChange={(_, value) => seek(value)}
              getAriaValueText={(value) => clock(value)}
            />
            <Typography variant="caption" color="text.secondary">
              {clock(media.time)} / {media.duration ? clock(media.duration) : '—'}
            </Typography>
          </Box>
          <IconButton
            aria-label="Forward 10 seconds"
            disabled={!media.ready || !media.duration}
            onClick={() => seek(media.time + 10)}
            sx={{ width: 44, height: 44 }}
          >
            <Iconify icon="material-symbols:forward-10-rounded" />
          </IconButton>
          <TextField
            select
            label="Speed"
            size="small"
            value={media.rate}
            onChange={(event) => setRate(Number(event.target.value))}
            sx={{ width: 88 }}
          >
            {[0.75, 1, 1.25, 1.5, 2].map((rate) => (
              <MenuItem key={rate} value={rate}>
                {rate}×
              </MenuItem>
            ))}
          </TextField>
          {!compact && <Box sx={{ width: 90, px: 1 }}>
            <Slider
              aria-label="Volume"
              value={media.volume}
              min={0}
              max={1}
              step={0.05}
              onChange={(_, value) => setVolume(value)}
            />
          </Box>}
        </Stack>
      )}
      {media.error && (
        <Alert severity="warning" action={<Button onClick={retry}>Retry audio</Button>}>
          {media.error}
        </Alert>
      )}
      {!compact && (
        <Button size="small" onClick={() => setNative(!native)} sx={{ alignSelf: 'flex-start' }}>
          {native ? 'Use enhanced controls' : 'Use browser audio controls'}
        </Button>
      )}
    </Stack>
  );
}
